// ============================================================
// AdBanner — Google AdSense banner component
//
// SETUP:
//   1. Replace YOUR_PUBLISHER_ID in index.html with your real AdSense
//      Publisher ID (looks like: ca-pub-1234567890123456)
//   2. Replace the adSlot prop values below with your real Ad Unit IDs
//      from https://adsense.google.com → Ads → By ad unit
//   3. After deploying, wait 24-48 hours for ads to start appearing
//
// AD SIZES:
//   leaderboard  — 728×90  (top/bottom of pages, desktop)
//   rectangle    — 300×250 (sidebar, between content)
//   mobile       — 320×50  (mobile top/bottom)
//   infeed       — responsive in-feed ad
// ============================================================
import React, { useEffect, useRef } from 'react';

// ── Default slot IDs — replace with your real Ad Unit IDs ──
const DEFAULT_SLOTS = {
  leaderboard : '1111111111',
  rectangle   : '2222222222',
  mobile      : '3333333333',
  infeed      : '4444444444',
};

// ── Master ads toggle — set to true when your AdSense account is approved ──
const ADS_ENABLED = true;

const isDev = process.env.NODE_ENV === 'development';
const publisherId = 'ca-pub-3381147914310838';

const AD_DIMENSIONS = {
  leaderboard : { width: '100%', height: '90px',  minWidth: '320px', label: 'Leaderboard 728×90' },
  rectangle   : { width: '300px', height: '250px', minWidth: '300px', label: 'Rectangle 300×250' },
  mobile      : { width: '100%', height: '50px',  minWidth: '320px', label: 'Mobile Banner 320×50' },
  infeed      : { width: '100%', height: '120px', minWidth: '300px', label: 'In-Feed Ad' },
};

const AdBanner = ({
  type    = 'leaderboard',  // leaderboard | rectangle | mobile | infeed
  adSlot,                   // override default slot id
  className = '',
  style = {},
  label = true,             // show "Advertisement" label above ad
}) => {
  const adRef = useRef(null);
  const slot = adSlot || DEFAULT_SLOTS[type] || DEFAULT_SLOTS.leaderboard;
  const dims = AD_DIMENSIONS[type] || AD_DIMENSIONS.leaderboard;

  useEffect(() => {
    if (!ADS_ENABLED || isDev) { return; }
    try {
      if (adRef.current && adRef.current.offsetWidth > 0) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {
      // AdSense not loaded yet or ad blocked
    }
  }, []);

  // ── Ads disabled globally ────────────────────────────────
  if (!ADS_ENABLED) { return null; }

  // ── Development placeholder ──────────────────────────────
  if (isDev) {
    return (
      <div
        className={`ad-banner ad-banner--dev ad-banner--${type} ${className}`}
        style={{
          width: dims.width,
          minWidth: dims.minWidth,
          height: dims.height,
          ...style,
        }}
        aria-label="Advertisement placeholder"
      >
        <div className="ad-banner__dev-inner">
          <span className="ad-banner__dev-label">📢 Ad Space</span>
          <span className="ad-banner__dev-type">{dims.label}</span>
          <span className="ad-banner__dev-hint">Real ads shown in production</span>
        </div>
      </div>
    );
  }

  // ── Production AdSense unit ──────────────────────────────
  return (
    <div
      className={`ad-banner ad-banner--${type} ${className}`}
      style={{ textAlign: 'center', overflow: 'hidden', ...style }}
    >
      {label && (
        <p className="ad-banner__label" aria-hidden="true">Advertisement</p>
      )}
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block', width: dims.width, height: dims.height }}
        data-ad-client={publisherId}
        data-ad-slot={slot}
        data-ad-format={type === 'infeed' ? 'fluid' : 'auto'}
        data-full-width-responsive="true"
      />
    </div>
  );
};

export default AdBanner;
